import { Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { FiatIcon, InfoIcon } from 'BreetAssets';
import { Dropdown, Tooltip, TooltipContent } from 'BreetComponents';
import { capitalizeFirstLetters, type ICurrencyDropDownItem, type IDropDownItem } from 'BreetHelpers';
import { useWalletBalance } from 'BreetHooks';
import { selectFiatMeta, useAppSelector } from 'BreetRedux';
import { type MouseEvent, useCallback } from 'react';

import type { ISelectWallet } from './SelectWallet.helpers';

const SelectWallet = ({
	selectWallet,
	selectedWallet,
	selectedDefaultWallet,
	selectDefaultWallet,
	title,
	toolTip,
	type,
}: ISelectWallet) => {
	const { getBalance } = useWalletBalance();

	const fiatMeta = useAppSelector(selectFiatMeta);

	const currencySelectedWallet: ICurrencyDropDownItem = {
		currency: fiatMeta.currency,
		walletBalance: getBalance(fiatMeta.currency),
	};

	const defaultSelectedWallet: IDropDownItem = {
		code: `${capitalizeFirstLetters(fiatMeta.currencyShort)} Wallet`,
		name: `${capitalizeFirstLetters(fiatMeta.currencyShort)} Wallet`,
		value: fiatMeta.currency,
		icon: (
			<FiatIcon
				currencyCode={fiatMeta.currency}
				variant='round'
			/>
		),
	};

	const handleItemSelect = useCallback(
		(item: ICurrencyDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			selectWallet?.(item);
		},
		[selectWallet]
	);

	const handleDefaultItemSelect = useCallback(
		(item: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation();
			selectDefaultWallet?.(item);
		},
		[selectDefaultWallet]
	);

	return (
		<div className='select-wallet-container '>
			<div className='header label-tooltip-header'>
				<Typography className='color-primary-100'>{title}</Typography>
				{toolTip && (
					<Tooltip tipElement={<TooltipContent content={toolTip} />}>
						<InfoIcon className='info-icon svg-dark' />
					</Tooltip>
				)}
			</div>
			<div className={classNameMerger('new-drop-down-input', type === 'withdraw' ? 'select-wallet' : 'fund-wallet-input')}>
				{type === 'withdraw' && (
					<Dropdown.ModernCurrency
						items={[
							{
								currency: fiatMeta.currency,
								walletBalance: getBalance(fiatMeta.currency),
							},
							{
								currency: 'USD',
								walletBalance: getBalance('USD'),
							},
						]}
						label=''
						hideSearch
						id='select-wallet-balance'
						selectedItem={selectedWallet ?? currencySelectedWallet}
						handleItemSelect={handleItemSelect}
					/>
				)}

				{type === 'fund' && (
					<Dropdown.Modern
						items={[
							{
								code: `${capitalizeFirstLetters(fiatMeta.currencyShort)} Wallet`,
								name: `${capitalizeFirstLetters(fiatMeta.currencyShort)} Wallet`,
								value: fiatMeta.currency,
								icon: (
									<FiatIcon
										currencyCode={fiatMeta.currency}
										variant='round'
									/>
								),
							},
							{
								code: 'USD Wallet',
								name: 'USD Wallet',
								value: 'USD',
								icon: (
									<FiatIcon
										currencyCode='USD'
										variant='round'
									/>
								),
							},
						]}
						label=''
						hideSearch
						id='fund-wallet-balance'
						selectedItem={selectedDefaultWallet ?? defaultSelectedWallet}
						handleItemSelect={handleDefaultItemSelect}
						CustomItemOption={DropdownItemAssets}
					/>
				)}
			</div>
		</div>
	);
};

export default SelectWallet;

const DropdownItemAssets = ({ item }: { item: IDropDownItem }) => (
	<>
		{item.icon}
		<span className='dropdown_content_item_options_item_name'>{item.code}</span>
	</>
);
